<template>
  <div class="not-prose mt-6 relative bg-white border border-gray-200 shadow-md rounded-lg py-16 px-4 lg:flex lg:items-center">
    <div class="absolute inset-x-0 top-0 transform translate-y-px">
      <div class="flex justify-center transform -translate-y-1/2">
        <span class="inline-flex rounded-xl bg-wiq-green px-4 py-1 text-sm font-semibold tracking-wider uppercase text-green-50">
          Club Pricing
        </span>
      </div>
    </div>
    <div class="max-w-lg mx-auto w-full space-y-8">
      <div class="grid grid-cols-1 gap-y-4">
        <div class="text-left ">
          <h2 class="uppercase text-base font-semibold tracking-widest text-gray-700 border-b-2 border-wiq-green inline-block">What makes WrestlingIQ pricing different?</h2>
        </div>
        <!-- No monthly fees section -->
        <div class="bg-green-50 border border-wiq-green rounded-lg p-4">
          <div class="flex items-start space-x-3">
            <svg class="flex-shrink-0 h-5 w-5 text-green-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p class="text-lg font-semibold text-green-900">$0 monthly fees</p>
              <p class="text-sm text-green-700">No subscription required</p>
            </div>
          </div>
          <div class="flex items-start space-x-3 mt-3">
            <svg class="flex-shrink-0 h-5 w-5 text-green-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p class="text-lg font-semibold text-green-900">$0 annual fees</p>
              <p class="text-sm text-green-700">No annual cost</p>
            </div>
          </div>
          <div class="flex items-start space-x-3 mt-3">
            <svg class="flex-shrink-0 h-5 w-5 text-green-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p class="text-lg font-semibold text-green-900">Free setup assistance</p>
              <p class="text-sm text-green-700">Optional - book a call with our expert team to get your account set up</p>
            </div>
          </div>
          <div class="flex items-start space-x-3 mt-3">
            <svg class="flex-shrink-0 h-5 w-5 text-green-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p class="text-lg font-semibold text-green-900">Website services add-on available</p>
              <p class="text-sm text-green-700">Optional - <a href="/feature/website" class="underline text-wiq-green">we offer full website services</a></p>
            </div>
          </div>
        </div>
        <div class="text-left mt-8">
          <h2 class="uppercase text-base font-semibold tracking-widest text-gray-700 border-b-2 border-wiq-green inline-block">What do clubs actually pay?</h2>
        </div>
        <div>
          <!-- How it works section -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div class="bg-blue-50 border-2 border-blue-200 rounded-lg overflow-hidden">
              <div class="bg-blue-600 text-blue-50 text-center py-1.5">
                <span class="text-sm font-bold tracking-widest uppercase">Club pays</span>
              </div>
              <div class="p-4">
                <p class="text-sm font-semibold text-gray-700 mb-2">Card Processing</p>
                <p class="text-3xl font-bold text-gray-900">2.9% + 30¢</p>
                <p class="text-sm text-gray-600 mt-2">Per successful transaction</p>
              </div>
            </div>
            <div class="bg-purple-50 border-2 border-purple-200 rounded-lg overflow-hidden">
              <div class="bg-purple-600 text-purple-50 text-center py-1.5">
                <span class="text-sm font-bold tracking-widest uppercase">Parent pays</span>
              </div>
              <div class="p-4">
                <p class="text-sm font-semibold text-gray-700 mb-2">WrestlingIQ Fee</p>
                <p class="text-3xl font-bold text-gray-900">2%</p>
                <p class="text-sm text-gray-600 mt-2">Added to each transaction</p>
              </div>
            </div>
          </div>

          <!-- Visual breakdown box -->
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p class="text-sm font-semibold text-gray-700 mb-3">Example: $100 camp registration</p>
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">You set registration price:</span>
                <span class="text-sm font-medium">$100.00</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">WIQ service fee is added:</span>
                <div class="text-right flex items-center gap-x-2">
                  <div class="text-xs text-purple-600 border border-purple-200 bg-purple-50 px-2 py-0.5 rounded-xl inline-block">+2%</div>
                  <span class="text-sm font-medium text-purple-600">$2.00</span>
                </div>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Parent pays:</span>
                <span class="text-sm font-medium">$102.00</span>
              </div>
              <div class="border-t border-gray-300 my-2"></div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Total for club:</span>
                <span class="text-sm font-medium text-green-600">$100.00</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Card processing:</span>
                <div class="text-right flex items-center gap-x-2">
                  <div class="text-xs text-blue-600 border border-blue-200 bg-blue-50 px-2 py-0.5 rounded-xl inline-block">2.9% + $0.30</div>
                  <span class="text-sm font-medium text-blue-600">-$3.26</span>
                </div>
              </div>
              <div class="border-t border-gray-300 my-2"></div>
              <div class="flex justify-between items-center">
                <span class="text-sm font-semibold text-gray-700">Club receives:</span>
                <span class="text-sm font-bold text-gray-900">$96.74</span>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-3 text-center">
              💡 No fees between transactions!
            </p>
          </div>
        </div>
      </div>
        <!-- Additional features -->
        <div class="bg-white border border-gray-200 rounded-lg p-6 mt-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Clubs receive all WIQ features including</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-5 h-5 bg-wiq-green rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="text-sm text-gray-700">Unlimited users</span>
            </div>
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-5 h-5 bg-wiq-green rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="text-sm text-gray-700">1,000 match videos</span>
            </div>
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-5 h-5 bg-wiq-green rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="text-sm text-gray-700">Unlimited registrations</span>
            </div>
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-5 h-5 bg-wiq-green rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="text-sm text-gray-700">Expert customer support from <a class="underline text-wiq-green" href="/about">our team</a></span>
            </div>
          </div>
          <div class="border-t border-gray-200 mt-4 pt-4">
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span class="text-sm text-gray-700">Free setup and digital strategy consultation (optional)</span>
            </div>
            <div class="flex items-start space-x-3 mt-3">
              <div class="flex-shrink-0 w-5 h-5  rounded-full flex items-center justify-center">
                <wrestler-icon override-class="w-3 h-3" strokeColor="#27aa83" />
              </div>
              <span class="text-sm text-gray-700">Support a business by wrestlers, for wrestlers</span>
            </div>
          </div>
        </div>

        <!-- CTA Button -->
        <div class="mt-6">
          <a href="/activate?team=club" class="block w-full bg-wiq-green hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg text-center text-lg transition-colors">
            Get started
          </a>
        </div>
    </div>
  </div>
</template>
<script>
import WrestlerIcon from '../../shared/wrestler-icon.vue';
export default {
  name: 'club-pricing',
  components: {
    WrestlerIcon
  },
};
</script>
